import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

interface CoinAnimationProps {
  className?: string;
}

const CoinAnimation: React.FC<CoinAnimationProps> = ({ className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationIdRef = useRef<number | null>(null);
  const isInteractingRef = useRef<boolean>(false);
  const initialRotationXRef = useRef<number>(0);
  const initialRotationYRef = useRef<number>(0);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const coinRef = useRef<THREE.Mesh | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Limpiar cualquier contenido existente
    while (containerRef.current.firstChild) {
      containerRef.current.removeChild(containerRef.current.firstChild);
    }

    // Initialize scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    
    const camera = new THREE.PerspectiveCamera(65, 1, 0.1, 1000);
    cameraRef.current = camera;
    camera.position.set(0, 0, 7);
    
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    rendererRef.current = renderer;
    
    // Responsive canvas size
    const getCanvasSize = () => {
      const width = window.innerWidth;
      if (width <= 480) return 300; // Mobile
      if (width <= 768) return 350; // Tablet
      return 400; // Desktop
    };
    
    const canvasSize = getCanvasSize();
    renderer.setSize(canvasSize, canvasSize);
    renderer.setClearColor(0x000000, 0);
    containerRef.current.appendChild(renderer.domElement);
    
    isInteractingRef.current = false;

    function resizeCanvasToDisplaySize() {
      const canvas = renderer.domElement;
      const newSize = getCanvasSize();
      
      if (canvas.width !== newSize || canvas.height !== newSize) {
        renderer.setSize(newSize, newSize, false);
        camera.aspect = 1;
        camera.updateProjectionMatrix();
      }
    }

    function onMouseMove(event: MouseEvent) {
      if (!isInteractingRef.current && coinRef.current) {
        const rect = renderer.domElement.getBoundingClientRect();
        const mouseX = event.clientX - rect.left - rect.width / 2;
        const mouseY = event.clientY - rect.top - rect.height / 2;

        const maxYRotationAngle = Math.PI / 4;
        const yRotationAngle = (mouseX / (rect.width / 2)) * maxYRotationAngle;

        const maxXRotationAngle = Math.PI / 4;
        const xRotationAngle = (mouseY / (rect.height / 2)) * maxXRotationAngle;

        coinRef.current.rotation.y = initialRotationYRef.current + yRotationAngle;
        coinRef.current.rotation.x = initialRotationXRef.current + xRotationAngle;
      }
    }

    // Touch events for mobile
    function onTouchMove(event: TouchEvent) {
      if (!isInteractingRef.current && coinRef.current) {
        event.preventDefault();
        const touch = event.touches[0];
        const rect = renderer.domElement.getBoundingClientRect();
        const touchX = touch.clientX - rect.left - rect.width / 2;
        const touchY = touch.clientY - rect.top - rect.height / 2;

        const maxYRotationAngle = Math.PI / 6; // Smaller for touch
        const yRotationAngle = (touchX / (rect.width / 2)) * maxYRotationAngle;

        const maxXRotationAngle = Math.PI / 6; // Smaller for touch
        const xRotationAngle = (touchY / (rect.height / 2)) * maxXRotationAngle;

        coinRef.current.rotation.y = initialRotationYRef.current + yRotationAngle;
        coinRef.current.rotation.x = initialRotationXRef.current + xRotationAngle;
      }
    }

    // Adding ambient lighting
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));

    // Left point light
    const pointLightLeft = new THREE.PointLight(0xff4422, 1);
    pointLightLeft.position.set(-1, -1, 3);
    scene.add(pointLightLeft);

    // Right point light
    const pointLightRight = new THREE.PointLight(0x44ff88, 1);
    pointLightRight.position.set(1, 2, 3);
    scene.add(pointLightRight);

    // Top point light
    const pointLightTop = new THREE.PointLight(0xdd3311, 1);
    pointLightTop.position.set(0, 3, 2);
    scene.add(pointLightTop);

    // Textures - Volver a la original pero en blanco y negro
    const textureCirc = new THREE.TextureLoader().load("https://m.media-amazon.com/images/I/81vOF2Rr2tS._AC_UF1000,1000_QL80_.jpg");
    const textureHeads = new THREE.TextureLoader().load("https://m.media-amazon.com/images/I/81vOF2Rr2tS._AC_UF1000,1000_QL80_.jpg");
    const textureTails = new THREE.TextureLoader().load("https://m.media-amazon.com/images/I/81vOF2Rr2tS._AC_UF1000,1000_QL80_.jpg");
    
    const metalness = 0.8;
    const roughness = 0.5;

    const materials = [
      new THREE.MeshStandardMaterial({
        map: textureCirc,
        metalness: metalness,
        roughness: roughness
      }),
      new THREE.MeshStandardMaterial({
        map: textureHeads,
        metalness: metalness,
        roughness: roughness
      }),
      new THREE.MeshStandardMaterial({
        map: textureTails,
        metalness: metalness,
        roughness: roughness
      })
    ];

    const geometry = new THREE.CylinderGeometry(3, 3, 0.4, 100);
    const coin = new THREE.Mesh(geometry, materials);
    coinRef.current = coin;

    scene.add(coin);
    camera.position.set(0, 0, 7);

    coin.rotation.x = Math.PI / 2;
    coin.rotation.y = Math.PI / 2;
    initialRotationXRef.current = coin.rotation.x;
    initialRotationYRef.current = coin.rotation.y;

    // Add event listeners for mouse interaction
    renderer.domElement.addEventListener("mousedown", () => (isInteractingRef.current = true));
    renderer.domElement.addEventListener("mouseup", () => (isInteractingRef.current = false));
    renderer.domElement.addEventListener("mouseleave", () => (isInteractingRef.current = false));
    renderer.domElement.addEventListener("mousemove", onMouseMove);
    
    // Touch events for mobile
    renderer.domElement.addEventListener("touchstart", () => (isInteractingRef.current = true), { passive: false });
    renderer.domElement.addEventListener("touchend", () => (isInteractingRef.current = false), { passive: false });
    renderer.domElement.addEventListener("touchmove", onTouchMove, { passive: false });

    function animateCoin(time: number) {
      time *= 0.0001;

      resizeCanvasToDisplaySize();

      if (coinRef.current) {
        coinRef.current.rotation.x += 0.008;
        coinRef.current.rotation.y += 0.0002;
      }

      renderer.render(scene, camera);
      animationIdRef.current = requestAnimationFrame(animateCoin);
    }

    // Start animation
    animateCoin(0);

    // Cleanup function
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      
      const container = containerRef.current;
      if (container && renderer.domElement) {
        container.removeChild(renderer.domElement);
      }
      
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
    };
  }, []);

  return (
    <div 
      ref={containerRef} 
      className={className}
      style={{ 
        width: '100%', 
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    />
  );
};

export default CoinAnimation; 